/**
 * UI preferences: apply + persist (localStorage cache + DB via API).
 * Same user login on another browser loads prefs from /auth/me | login.
 */

import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import {
  applyShellTheme,
  setStoredShellTheme,
  getStoredShellTheme,
  DEFAULT_SHELL_THEME,
  isValidShellTheme,
} from './shellTheme';
import {
  applySidebarMode,
  setStoredSidebarMode,
  DEFAULT_SIDEBAR_MODE,
  isValidSidebarMode,
  resolveAccountSidebarMode,
} from './sidebarMode';
import {
  DEFAULT_LANDING_VIEW,
  isValidLandingView,
} from './landingView';
import {
  DEFAULT_COMPLAINTS_STATUS_FILTER,
  DEFAULT_DEVICES_FILTER,
  DEFAULT_COMPLAINTS_SORT,
  DEFAULT_DEVICES_SORT,
  isValidComplaintsStatusFilter,
  isValidDevicesFilter,
  isValidComplaintsSort,
  isValidDevicesSort,
} from './listDefaults';
import { applyOrgBrandingFromUser } from './orgBranding';

export const LIST_PAGE_SIZES = [10, 25, 50, 100];
export const DEFAULT_LIST_PAGE_SIZE = 25;
export const ALERT_VIEW_MODES = [
  { id: 'active', label: 'Active' },
  { id: 'acknowledged', label: 'Acknowledged' },
  { id: 'snoozed', label: 'Snoozed' },
  { id: 'archived', label: 'Archived' },
];
export const DEFAULT_ALERTS_VIEW_MODE = 'active';
export const DEFAULT_LOGIN_ALERTS_TOAST = false;
export { DEFAULT_LANDING_VIEW };
export {
  DEFAULT_COMPLAINTS_STATUS_FILTER,
  DEFAULT_DEVICES_FILTER,
  DEFAULT_COMPLAINTS_SORT,
  DEFAULT_DEVICES_SORT,
};

const LIST_PAGE_SIZE_KEY = 'list-page-size';
const ALERTS_VIEW_MODE_KEY = 'alerts-view-mode';
const LANDING_VIEW_KEY = 'landing-view';
const LOGIN_ALERTS_TOAST_KEY = 'login-alerts-toast';
const COMPLAINTS_STATUS_FILTER_KEY = 'complaints-status-filter';
const DEVICES_FILTER_KEY = 'devices-filter';
const COMPLAINTS_SORT_KEY = 'complaints-sort';
const DEVICES_SORT_KEY = 'devices-sort';

/** Same-tab signal when shell/nav (or other) prefs are applied to the DOM. */
export const UI_PREFERENCES_EVENT = 'mdm-ui-preferences';

function syncSessionUserPreferences(partial) {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return;
    const sessionUser = JSON.parse(raw);
    sessionUser.ui_preferences = {
      ...(sessionUser.ui_preferences || {}),
      ...partial,
    };
    sessionStorage.setItem('user', JSON.stringify(sessionUser));
  } catch {
    /* ignore */
  }
}

function notifyUiPreferencesApplied(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(UI_PREFERENCES_EVENT, { detail }));
  } catch {
    /* ignore */
  }
}

function readUserPrefs() {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.ui_preferences || null;
  } catch {
    return null;
  }
}

export function isValidListPageSize(value) {
  const n = parseInt(value, 10);
  return LIST_PAGE_SIZES.includes(n);
}

export function isValidAlertsViewMode(value) {
  return ALERT_VIEW_MODES.some((m) => m.id === value);
}

export function isValidLoginAlertsToast(value) {
  return typeof value === 'boolean';
}

export function getListPageSize() {
  const fromUser = readUserPrefs()?.list_page_size;
  if (isValidListPageSize(fromUser)) return parseInt(fromUser, 10);
  try {
    const saved = localStorage.getItem(LIST_PAGE_SIZE_KEY);
    if (isValidListPageSize(saved)) return parseInt(saved, 10);
  } catch {
    /* ignore */
  }
  return DEFAULT_LIST_PAGE_SIZE;
}

export function getAlertsViewMode() {
  const fromUser = readUserPrefs()?.alerts_view_mode;
  if (isValidAlertsViewMode(fromUser)) return fromUser;
  try {
    const saved = localStorage.getItem(ALERTS_VIEW_MODE_KEY);
    if (isValidAlertsViewMode(saved)) return saved;
  } catch {
    /* ignore */
  }
  return DEFAULT_ALERTS_VIEW_MODE;
}

export function getLandingView() {
  const fromUser = readUserPrefs()?.landing_view;
  if (isValidLandingView(fromUser)) return fromUser;
  try {
    const saved = localStorage.getItem(LANDING_VIEW_KEY);
    if (isValidLandingView(saved)) return saved;
  } catch {
    /* ignore */
  }
  return DEFAULT_LANDING_VIEW;
}

export function getLoginAlertsToast() {
  const fromUser = readUserPrefs()?.login_alerts_toast;
  if (isValidLoginAlertsToast(fromUser)) return fromUser;
  try {
    const saved = localStorage.getItem(LOGIN_ALERTS_TOAST_KEY);
    if (saved === 'true') return true;
    if (saved === 'false') return false;
  } catch {
    /* ignore */
  }
  return DEFAULT_LOGIN_ALERTS_TOAST;
}

function getStoredPref(key, isValid, fallback) {
  const fromUser = readUserPrefs()?.[key];
  if (isValid(fromUser)) return fromUser;
  try {
    const storageKey = {
      complaints_status_filter: COMPLAINTS_STATUS_FILTER_KEY,
      devices_filter: DEVICES_FILTER_KEY,
      complaints_sort: COMPLAINTS_SORT_KEY,
      devices_sort: DEVICES_SORT_KEY,
    }[key];
    const saved = storageKey ? localStorage.getItem(storageKey) : null;
    if (isValid(saved)) return saved;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function getComplaintsStatusFilter() {
  return getStoredPref(
    'complaints_status_filter',
    isValidComplaintsStatusFilter,
    DEFAULT_COMPLAINTS_STATUS_FILTER
  );
}

export function getDevicesFilter() {
  return getStoredPref('devices_filter', isValidDevicesFilter, DEFAULT_DEVICES_FILTER);
}

export function getComplaintsSort() {
  return getStoredPref('complaints_sort', isValidComplaintsSort, DEFAULT_COMPLAINTS_SORT);
}

export function getDevicesSort() {
  return getStoredPref('devices_sort', isValidDevicesSort, DEFAULT_DEVICES_SORT);
}

export function setStoredListPageSize(size) {
  if (!isValidListPageSize(size)) return;
  try {
    localStorage.setItem(LIST_PAGE_SIZE_KEY, String(size));
    // Keep legacy session keys in sync for older list code paths
    sessionStorage.setItem('devicesPerPage', String(size));
    sessionStorage.setItem('complaintsPerPage', String(size));
  } catch {
    /* ignore */
  }
}

export function setStoredAlertsViewMode(mode) {
  if (!isValidAlertsViewMode(mode)) return;
  try {
    localStorage.setItem(ALERTS_VIEW_MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function setStoredLandingView(view) {
  if (!isValidLandingView(view)) return;
  try {
    localStorage.setItem(LANDING_VIEW_KEY, view);
  } catch {
    /* ignore */
  }
}

export function setStoredLoginAlertsToast(enabled) {
  if (!isValidLoginAlertsToast(enabled)) return;
  try {
    localStorage.setItem(LOGIN_ALERTS_TOAST_KEY, enabled ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

export function setStoredComplaintsStatusFilter(value) {
  if (!isValidComplaintsStatusFilter(value)) return;
  try {
    localStorage.setItem(COMPLAINTS_STATUS_FILTER_KEY, value);
  } catch {
    /* ignore */
  }
}

export function setStoredDevicesFilter(value) {
  if (!isValidDevicesFilter(value)) return;
  try {
    localStorage.setItem(DEVICES_FILTER_KEY, value);
  } catch {
    /* ignore */
  }
}

export function setStoredComplaintsSort(value) {
  if (!isValidComplaintsSort(value)) return;
  try {
    localStorage.setItem(COMPLAINTS_SORT_KEY, value);
  } catch {
    /* ignore */
  }
}

export function setStoredDevicesSort(value) {
  if (!isValidDevicesSort(value)) return;
  try {
    localStorage.setItem(DEVICES_SORT_KEY, value);
  } catch {
    /* ignore */
  }
}

export function applyUiPreferences(prefs = {}, options = {}) {
  const syncSidebar = options.syncSidebar !== false;
  const shell = isValidShellTheme(prefs.shell_theme)
    ? prefs.shell_theme
    : getStoredShellTheme() || DEFAULT_SHELL_THEME;
  const sidebarMode = resolveAccountSidebarMode(prefs);
  const listSize = isValidListPageSize(prefs.list_page_size)
    ? parseInt(prefs.list_page_size, 10)
    : getListPageSize();
  const alertsMode = isValidAlertsViewMode(prefs.alerts_view_mode)
    ? prefs.alerts_view_mode
    : getAlertsViewMode();
  const landing = isValidLandingView(prefs.landing_view)
    ? prefs.landing_view
    : getLandingView();
  const loginAlertsToast = isValidLoginAlertsToast(prefs.login_alerts_toast)
    ? prefs.login_alerts_toast
    : getLoginAlertsToast();
  const complaintsStatus = isValidComplaintsStatusFilter(prefs.complaints_status_filter)
    ? prefs.complaints_status_filter
    : getComplaintsStatusFilter();
  const devicesFilter = isValidDevicesFilter(prefs.devices_filter)
    ? prefs.devices_filter
    : getDevicesFilter();
  const complaintsSort = isValidComplaintsSort(prefs.complaints_sort)
    ? prefs.complaints_sort
    : getComplaintsSort();
  const devicesSort = isValidDevicesSort(prefs.devices_sort)
    ? prefs.devices_sort
    : getDevicesSort();

  applyShellTheme(shell);
  setStoredShellTheme(shell);
  // Always cache the account default; only push it to the live shell when requested
  setStoredSidebarMode(sidebarMode);
  syncSessionUserPreferences({
    shell_theme: shell,
    sidebar_mode: sidebarMode,
    sidebar_mode_explicit: prefs.sidebar_mode_explicit === true,
  });
  if (syncSidebar) {
    applySidebarMode(sidebarMode);
  }
  setStoredListPageSize(listSize);
  setStoredAlertsViewMode(alertsMode);
  setStoredLandingView(landing);
  setStoredLoginAlertsToast(loginAlertsToast);
  setStoredComplaintsStatusFilter(complaintsStatus);
  setStoredDevicesFilter(devicesFilter);
  setStoredComplaintsSort(complaintsSort);
  setStoredDevicesSort(devicesSort);
  notifyUiPreferencesApplied({
    shell_theme: true,
    sidebar_mode: syncSidebar,
  });

  return {
    shell_theme: shell,
    sidebar_mode: sidebarMode,
    list_page_size: listSize,
    alerts_view_mode: alertsMode,
    landing_view: landing,
    login_alerts_toast: loginAlertsToast,
    complaints_status_filter: complaintsStatus,
    devices_filter: devicesFilter,
    complaints_sort: complaintsSort,
    devices_sort: devicesSort,
  };
}

export function applyUiPreferencesFromUser(user) {
  applyOrgBrandingFromUser(user);
  if (user?.ui_preferences) {
    return applyUiPreferences(user.ui_preferences);
  }
  // No account prefs: use platform defaults (do not inherit another user's local cache)
  return applyUiPreferences({
    shell_theme: DEFAULT_SHELL_THEME,
    sidebar_mode: DEFAULT_SIDEBAR_MODE,
    sidebar_mode_explicit: false,
    list_page_size: DEFAULT_LIST_PAGE_SIZE,
    alerts_view_mode: DEFAULT_ALERTS_VIEW_MODE,
    landing_view: DEFAULT_LANDING_VIEW,
    login_alerts_toast: DEFAULT_LOGIN_ALERTS_TOAST,
    complaints_status_filter: DEFAULT_COMPLAINTS_STATUS_FILTER,
    devices_filter: DEFAULT_DEVICES_FILTER,
    complaints_sort: DEFAULT_COMPLAINTS_SORT,
    devices_sort: DEFAULT_DEVICES_SORT,
  });
}

/**
 * Persist a partial preference patch to DB (and localStorage immediately).
 * Fire-and-forget safe: local apply always happens; network errors are logged.
 */
export async function saveUiPreferencePatch(patch) {
  const changed = {};
  if (patch.shell_theme) {
    setStoredShellTheme(patch.shell_theme);
    applyShellTheme(patch.shell_theme);
    changed.shell_theme = true;
  }
  if (patch.sidebar_mode) {
    setStoredSidebarMode(patch.sidebar_mode);
    applySidebarMode(patch.sidebar_mode);
    syncSessionUserPreferences({
      sidebar_mode: patch.sidebar_mode,
      sidebar_mode_explicit: true,
    });
    changed.sidebar_mode = true;
  }
  // nav_theme is ignored — sidebar chrome uses fixed tokens
  if (changed.shell_theme || changed.sidebar_mode) {
    notifyUiPreferencesApplied(changed);
  }
  if (patch.list_page_size != null) {
    setStoredListPageSize(patch.list_page_size);
  }
  if (patch.alerts_view_mode) {
    setStoredAlertsViewMode(patch.alerts_view_mode);
  }
  if (patch.landing_view) {
    setStoredLandingView(patch.landing_view);
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'login_alerts_toast')) {
    setStoredLoginAlertsToast(Boolean(patch.login_alerts_toast));
  }
  if (patch.complaints_status_filter) {
    setStoredComplaintsStatusFilter(patch.complaints_status_filter);
  }
  if (patch.devices_filter) {
    setStoredDevicesFilter(patch.devices_filter);
  }
  if (patch.complaints_sort) {
    setStoredComplaintsSort(patch.complaints_sort);
  }
  if (patch.devices_sort) {
    setStoredDevicesSort(patch.devices_sort);
  }

  const token = sessionStorage.getItem('token');
  if (!token) return null;

  // Do not send obsolete nav_theme patches to the server from this client
  const { nav_theme: _ignoredNavTheme, ...serverPatch } = patch;
  if (!Object.keys(serverPatch).length) return null;

  try {
    const response = await axios.put(
      API_ENDPOINTS.AUTH.PREFERENCES,
      serverPatch,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const prefs = response.data?.ui_preferences;
    if (prefs) {
      // Backdrop (etc.) must not reset the live ☰ session density
      applyUiPreferences(prefs, { syncSidebar: Boolean(patch.sidebar_mode) });
      try {
        const raw = sessionStorage.getItem('user');
        if (raw) {
          const user = JSON.parse(raw);
          user.ui_preferences = prefs;
          sessionStorage.setItem('user', JSON.stringify(user));
        }
      } catch {
        /* ignore */
      }
    }
    return prefs;
  } catch (err) {
    console.warn('[uiPreferences] Failed to save to server:', err?.message || err);
    return null;
  }
}
